'use strict';

define('vb/extensions/dynamic/private/types/abstractMetadataProviderDescriptor',[
  'vb/private/types/factoryMetadataProviderDescriptor',
],
(FactoryMetadataProviderDescriptor) => {
  /**
   * @typedef {import(
   *   'vb/extensions/dynamic/private/types/bridges/metadataProviderFactoryBridge'
   * ).default} MetadataProviderFactoryBridge
   */

  /**
   *
   */
  class AbstractMetadataProviderDescriptor extends FactoryMetadataProviderDescriptor {
    constructor() {
      super();

      /**
       * @type {MetadataProviderFactoryBridge|null}
       */
      this.bridge = null;
    }

    /**
     * This method is called during the activation phase of the descriptor. During this phase,
     * it is safe to access values containing expressions to other variables.
     *
     * @returns {Promise<void>}
     */
    activate() {
      return Promise.resolve()
        .then(() => {
          // We used to override the default value here to remove any expressions but with VBS-33116,
          // we need to make the metadata descriptor behave more like a variable so changes to its properties
          // via expressions can be tracked.

          // make sure our bridge is created
          this.getFactoryBridge();

          return super.activate();
        });
    }

    /**
     * no need to override, sets 'this.bridge'. subclasses should override createFactoryBridge()
     * @returns {MetadataProviderFactoryBridge}
     */
    getFactoryBridge() {
      if (!this.bridge) {
        this.bridge = this.createFactoryBridge();
      }
      return this.bridge;
    }

    /**
     * return the VB factory implementation that will act as a bridge to the JET factory
     * subclasses must override
     * @returns {MetadataProviderFactoryBridge}
     */
    // eslint-disable-next-line class-methods-use-this
    createFactoryBridge() {
      throw new Error('do not use directly, createFactoryBridge() must be implemented');
    }

    /**
     * return the VB type definition
     * subclasses must override
     * @returns {Object}
     */
    // eslint-disable-next-line class-methods-use-this
    getTypeDefinition() {
      throw new Error('do not use directly, getTypeDefinition() must be implemented');
    }

    /**
     * Overrides the base implementation, which normally uses the `factory` property.
     *
     * @see FactoryMetadataProviderDescriptor#getProviderFactory
     * @returns {Promise<MetadataProviderFactoryBridge>}
     */
    // eslint-disable-next-line no-unused-vars
    getProviderFactory(factoryPath) {
      return Promise.resolve(this.getFactoryBridge());
    }

    /**
     *
     * duck-types createMetadataProvider from JET factory interface.
     * this is invoked when the Variable type is being used as the "factory" parameter
     * for a FactoryMetadataProviderDescriptor.
     *
     * The FMPD uses this class as a factory, and this method in turns creates a 'bridge' factory if it hasn't been
     * created already, and then delegates creation of the MetadataProvider.
     *
     * The bridge always translated VB-specific options to JET ones.
     *
     * @param {Object} options
     * @return {Promise<Object>}
     */
    createMetadataProvider(options) {
      // make sure our bridge is created
      return this.getFactoryBridge().createMetadataProvider(options);
    }

    dispose() {
      if (this.bridge) {
        this.bridge.dispose();
      }
      // Call dispose last so that bridge property is not deleted
      super.dispose();
    }
  }

  return AbstractMetadataProviderDescriptor;
});

